import { commonUploadSignature } from "@server/commonApi";
import { storage } from "@utils/storage";
import { getRandomString, showLoading, showToast, showToastAsync, sleep, whileArray } from "@utils/utils";
import realtimeLog from "@utils/realtimeLog";
import { isWxH5 } from "@utils/business";
import { wxH5ChooseImage } from "@utils/officialAccount";

/**
 * 获取上传文件信息签名
 * @returns {Promise<*>}
 */
export const getUploadSignature = async () => {
    const _u = storage.get('uploadSignature');
    if (_u) return _u;
    const res = await commonUploadSignature();
    if (!res.$success) return;
    storage.set('uploadSignature', res.data, { duration: 60 * 60 * 24 });
    return res.data;
}

/**
 * 清空上传图片签名
 */
const clearUploadSignature = () => {
    storage.remove('uploadSignature');
}


/**
 * 选择图片
 * @param options
 * @returns {Promise<UniNamespace.ChooseImageSuccessCallbackResultFile|UniNamespace.ChooseImageSuccessCallbackResultFile[]|File|File[]|null>}
 */
export const chooseImages = async (options = {}) => {
    try {
        if (isWxH5()) {
            const d = await wxH5ChooseImage(options)
            if (!d) return null;
            return [{ path: d }]
        }
        const res = await uni.chooseImage(options);
        if (res.errMsg.indexOf('ok') > -1) {
            return res.tempFiles
        }
        return null;
    } catch (e) {
        console.error(e);
        return null;
    }
}

/**
 * 压缩图片
 * @param src
 * @param quality
 * @returns {Promise<unknown>}
 */
export const compressImage = (src, quality = 80) => {
    return new Promise(resolve => {
        uni.compressImage({
            src,
            quality,
            success: res => {
                if (res.errMsg.indexOf('ok') > -1) {
                    resolve(res.tempFilePath)
                    return
                }
                resolve(null);
            },
            fail: (e) => {
                console.error(e);
                resolve(null)
            }
        })
    })
}

/**
 * 上传文件
 * @param fileInfo
 * @param options
 * @returns {Promise<unknown>}
 * @private
 */
export const _uploadFile = (fileInfo, options = {}) => {
    return new Promise(async resolve => {
        const uploadSignature = await getUploadSignature();
        const uploadQuery = {
            url: uploadSignature.uploadDomain,
            filePath: fileInfo.path,
            name: 'file',
            timeout: options.timeout || 10000,
            formData: {
                key: uploadSignature.dir + fileInfo.name,
                policy: uploadSignature.policy,
                OSSAccessKeyId: uploadSignature.accessid,
                signature: uploadSignature.signature,
                success_action_status: '200',
            }
        }
        try {
            const uploadInfo = await uni.uploadFile(uploadQuery);
            console.error('uploadFile上传文件参数:')
            console.error("uploadSignature--", uploadSignature);
            console.error("uploadQuery--", uploadQuery);
            console.error("fileInfo--", fileInfo);
            console.error("uploadInfo--", uploadInfo);
            if (uploadInfo.statusCode === 200) {
                const _d = uploadInfo.errMsg.indexOf('ok') > -1 ? `${uploadSignature.accessDomain}/${uploadQuery.formData.key}` : null;
                !_d && realtimeLog.error('uploadFile上传文件参数:', "--uploadSignature--", uploadSignature, "--uploadQuery--", uploadQuery, "--fileInfo--", fileInfo, "--uploadInfo--", uploadInfo)
                resolve(_d);
            } else {
                realtimeLog.error('uploadFile上传文件参数:', "--uploadSignature--", uploadSignature, "--uploadQuery--", uploadQuery, "--fileInfo--", fileInfo, "--uploadInfo--", uploadInfo)
                clearUploadSignature();
                resolve(null);
            }
        } catch (e) {
            console.error(e);
            console.error('uploadFile异常:')
            console.error("uploadSignature--", uploadSignature);
            console.error("fileInfo--", fileInfo);
            realtimeLog.error('uploadFile异常:', "--uploadSignature--", uploadSignature, "--fileInfo--", fileInfo, '--e--', e)
            clearUploadSignature();
            resolve(null);
        }
    })
}

/**
 * 从base64编码中解析图片信息
 * @param {String} base64
 * @returns {Object}
 */
function getFileTypeByBase64(base64) {
    let re = new RegExp('data:(?<type>.*?);base64,(?<data>.*)')
    let res = re.exec(base64)

    if (res) {
        return res.groups.type.split('/').slice(-1)[0]
        // type: res.groups.type,
        // ext: res.groups.type.split('/').slice(-1)[0],
        // data: res.groups.data,
    }
}

/**
 * 获取图片信息
 * @param src
 * @returns {Promise<UniNamespace.GetImageInfoSuccessData|null>}
 */
export const getImageInfo = async (file) => {
    try {
        const res = await uni.getImageInfo({ src: file.path });
        if (!res.type) {
            if (isWxH5()) {
                res.type = getFileTypeByBase64(file.path);
            } else {
                res.type = getFileType(file.name);
            }
        }
        return res;
    } catch (e) {
        console.error(e)
        return null;
    }
}

/**
 * 获取文件类型
 * @param fileName
 * @returns {string}
 */
export const getFileType = (fileName) => {
    const regex = /(?:\.([^.]+))?$/;
    return regex.exec(fileName)[1];
}

/**
 * 生成文件名称
 * @param file
 * @param length
 * @returns {`${number}.${string}`}
 */
export const getFileName = (file, length = 4) => {
    return `${getRandomString(length)}${+new Date()}.${file.type}`
}

/**
 * 上传图片
 * @param options - 选择图片参数
 * @param format - 格式化图片函数，默认不传
 * @returns {Promise<*[]|null>}
 */
export const uploadImg = async (options = {}, format = null) => {
    // 选择图片
    let checkImages = await chooseImages(options);
    if (!checkImages || checkImages.length === 0) {
        return null
    }
    // 对部分机型，设置 count 失效进行兼容
    if (checkImages.length > options.count) {
        checkImages.length = options.count;
    }

    let hideLoading = showLoading()

    // 获取图片信息
    await whileArray(checkImages, async (item, i) => {
        let imageInfo = item;

        // #ifdef H5
        imageInfo = await getImageInfo(item);
        // #endif

        checkImages[i] = { ...item, ...imageInfo, name: getFileName(imageInfo, 4) };
    });

    // 压缩图片失败
    let compressImageError = false;

    // 压缩图片
    await whileArray(checkImages, async (item, i) => {
        if (item.size > ((options.maxSize || 5) * 1024 * 1024)) {
            if (compressImageError) return;
            let path = await compressImage(item.path, options.quality || 80);
            checkImages[i].path = path;
            // 这里为图片压缩失败
            if (!path) {
                compressImageError = true;
            }
        }
    });

    // 如果存在压缩图片信息失败
    if (compressImageError) {
        hideLoading();
        showToast('部分图片压缩失败，请检查文件大小~');
        return null
    }

    if (format) {
        hideLoading();
        checkImages = await format(checkImages)
        hideLoading = showLoading()
    }

    let images = [];
    // 上传图片
    await whileArray(checkImages, async (item) => {
        const imageInfo = await _uploadFile(item, { timeout: 30000 })
        imageInfo && images.push(imageInfo);
    });
    hideLoading();
    if (checkImages.length !== images.length) {
        showToast('部分图片上传失败，请检查重试~');
        await sleep(3000)
    }
    return images;
}

/**
 * 选择视频
 * @param options
 * @returns {Promise<UniNamespace.ChooseVideoSuccess|null>}
 */
const chooseVideo = async (options = {}) => {
    try {
        const res = await uni.chooseVideo(options);
        if (res.errMsg.indexOf('ok') > -1) {
            return res
        }
        return null;
    } catch (e) {
        console.error(e);
        return null;
    }
}

/**
 * 获取视频名称
 * @param path
 * @returns {*}
 */
const getVideoName = (path) => {
    const data = path.split('/');
    return data[data.length - 1];
}

/**
 * 上传视频
 * @param options
 * @returns {Promise<*[]|null>}
 */
export const uploadVideo = async (options = {}) => {
    // 选择视频
    let checkVideo = await chooseVideo(options);
    if (!checkVideo) {
        return null
    }

    const maxSize = options.maxSize || 50;

    // #ifdef MP-WEIXIN
    if (options.hasOwnProperty('minDuration')) {
        if (checkVideo.duration <= options.minDuration) {
            await showToastAsync(`视频录制时间太短,请至少录制${options.minDuration}秒以上`, { duration: 3000 })
            return null;
        }
    }

    if (options.hasOwnProperty('maxDuration')) {
        if (checkVideo.duration > options.maxDuration) {
            await showToastAsync(`视频录制时间太长,最长录制${options.maxDuration}秒`, { duration: 3000 })
            return null;
        }
    }
    // #endif

    if (checkVideo.size > (maxSize * 1024 * 1024)) {
        await showToastAsync(`视频过大,最多支持上传${maxSize}m`)
        return null;
    }

    checkVideo.path = checkVideo.tempFilePath;
    checkVideo.name = getVideoName(checkVideo.tempFilePath);
    const hideLoading = showLoading()
    // 上传视频
    const src = await _uploadFile(checkVideo, { timeout: 30000 })
    hideLoading();
    if (!src) {
        console.error('视频上传失败，请检查重试~');
        showToast('视频上传失败，请检查重试~');
        await sleep(3000)
    }
    return {
        cover: src + "?x-oss-process=video/snapshot,t_2000,f_jpg,w_0,h_0,m_fast,ar_auto",
        src
    };
}
